import type { ToolResult as Result } from '../../lib/messageContent'
import Markdown from './Markdown'

// Tools put their readable output under one of these; errors use `message`.
const PRIMARY_KEYS = ['analysis', 'result', 'summary', 'text', 'message', 'output']
// `attachments` render as image previews elsewhere — don't repeat them as JSON.
const OMIT_FROM_TAIL = new Set([...PRIMARY_KEYS, 'attachments'])

function split(raw: string) {
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return { body: raw, tail: '' }
  }

  if (typeof parsed === 'string') return { body: parsed, tail: '' }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return { body: '', tail: JSON.stringify(parsed, null, 2) }
  }

  const fields = parsed as Record<string, unknown>
  const primary = PRIMARY_KEYS.find((key) => typeof fields[key] === 'string' && fields[key])
  const rest = Object.entries(fields).filter(
    ([key, value]) =>
      !OMIT_FROM_TAIL.has(key) &&
      value != null &&
      value !== '' &&
      !(Array.isArray(value) && value.length === 0),
  )

  return {
    body: primary ? (fields[primary] as string) : '',
    tail: rest.length > 0 ? JSON.stringify(Object.fromEntries(rest), null, 2) : '',
  }
}

export default function ToolResult({ result }: { result: Result }) {
  const { body, tail } = split(result.content)

  return (
    <div
      className={`max-h-56 space-y-1 overflow-y-auto rounded border px-1.5 py-1 text-[10px] leading-relaxed [scrollbar-width:thin] ${
        result.isError ? 'border-rose-200 bg-rose-50/40 text-rose-900' : 'border-stone-200 bg-stone-100 text-stone-700'
      }`}
    >
      {body && <Markdown text={body} />}
      {tail && (
        <pre className="font-mono break-words whitespace-pre-wrap text-stone-500">
          {tail}
        </pre>
      )}
    </div>
  )
}
